import type { ApolloCache, ApolloClient } from "@apollo/client";
import { FIELD_TYPES } from "@salestrak/custom-fields";
import {
  CustomFieldDefinitionFragmentDoc,
  IssueValuesFragmentDoc,
  IssueViewMovedDocument,
  IssueViewUpdatedDocument,
} from "#app/lib/data";
import {
  defineSubscription,
  type AnySubscriptionRequest,
} from "#app/lib/subscriptions/subscription-provider";
import {
  identifyIssueInCache,
  writeIssueToCacheViaClient,
} from "#app/hooks/kanban/kanban-cache-utils";

// ---------------------------------------------------------------------------
// Issue-view realtime — registered by the issue view for the open issue's
// workflow, using the dialog-scoped IssueViewUpdated/IssueViewMoved documents
// (issue-view.graphql). Values ride these documents only: the shared
// board/list documents stay values-free because Issue.values is a
// per-subscriber @ResolveField over every custom_field_value row, and this
// registration exists only while a dialog is open.
//
// Both events are handled because the server publishes ISSUE_MOVED xor
// ISSUE_UPDATED — a save that edits fields AND changes stage publishes only
// the former, and the open dialog must not go stale on it. Column membership
// on a mounted board stays the board's own IssueMoved handler's job
// (kanban-subscriptions.ts); this handler writes the entity only.
// ---------------------------------------------------------------------------

/** A photos-type value is delta-managed: the add/removeIssuePhotoFieldValue
 *  mutations are its ONLY writer (their commutative cache.modify writes live
 *  in use-issue-view-mutations), and they publish no subscription event. So a
 *  photos entry in an updateIssue payload is a DB snapshot that may predate
 *  deltas already applied locally (in-flight uploads) — and can never carry
 *  photo news the deltas didn't. Field type comes from the normalized
 *  CustomFieldDefinition the open dialog's IssueViewData query cached; an
 *  unknown field (not cached) is treated as not-photos, which only matters
 *  for issues with no dialog open — no local deltas can be in flight there. */
function isPhotosField(cache: ApolloCache, customFieldId: string): boolean {
  const cacheId = cache.identify({ __typename: "CustomField", customFieldId });
  if (!cacheId) return false;
  const def = cache.readFragment({
    id: cacheId,
    fragment: CustomFieldDefinitionFragmentDoc,
  });
  return def?.type === FIELD_TYPES.photos;
}

/** Write the payload's custom-field values, merged per field: photos-type
 *  entries keep the locally-cached value (see isPhotosField), every other
 *  field takes the incoming value — updateIssue is its only writer, so the
 *  payload is authoritative for it. */
function writeIssueValuesToCache<
  T extends { __typename: "Issue"; id: string; issueId: string; values?: unknown },
>(client: ApolloClient, issue: T): void {
  const cacheId = identifyIssueInCache(client.cache, issue.issueId);
  if (!cacheId) return;

  const cached = client.cache.readFragment({
    id: cacheId,
    fragment: IssueValuesFragmentDoc,
  });
  const incoming = (issue.values ?? null) as Record<string, unknown> | null;
  const local = (cached?.values ?? null) as Record<string, unknown> | null;

  let values: Record<string, unknown> | null = incoming;
  if (local) {
    const merged: Record<string, unknown> = { ...(incoming ?? {}) };
    for (const [fieldId, value] of Object.entries(local)) {
      if (isPhotosField(client.cache, fieldId)) merged[fieldId] = value;
    }
    values = merged;
  }

  client.cache.writeFragment({
    id: cacheId,
    fragment: IssueValuesFragmentDoc,
    data: { ...issue, values },
  });
}

export function issueViewSubscriptions(
  workflowId: string,
): AnySubscriptionRequest[] {
  const variables = { workflowId };
  return [
    defineSubscription({
      document: IssueViewUpdatedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueUpdated;
        if (!event) return;
        // Summary fields (name, assignees, dates, …) — the same write the
        // board and list surfaces perform.
        writeIssueToCacheViaClient(client, event.issue);
        writeIssueValuesToCache(client, event.issue);
      },
    }),
    defineSubscription({
      document: IssueViewMovedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueMoved;
        if (!event) return;
        writeIssueToCacheViaClient(client, event.issue);
        writeIssueValuesToCache(client, event.issue);
      },
    }),
  ];
}
